import { useState, useMemo, useEffect } from "react";
import MaterialReactTable from "material-react-table";
import { GlobalStyles } from "@mui/material";

const CustomMaterialTable = ({
  columns,
  data,
  darkMode,
  exportExcel,
  setVisibleColumns,
  setFilteredData,
  toggleDensity,
  initialState: incomingInitialState = {},
  ...rest
}) => {
  const [columnVisibility, setColumnVisibility] = useState(
    (incomingInitialState && incomingInitialState.columnVisibility) || {}
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);

  const normalizeText = (value) => {
    if (value === null || value === undefined) return "";

    return String(value)
      .toLowerCase()
      .replace(/[-_]+/g, " ")   // treat dash & underscore as space
      .replace(/\s+/g, " ")
      .trim();   // collapse multiple spaces
  };

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalizeText(globalFilter);
    // const searchWords = normalizedSearch
    //   .split(/[^a-z0-9]+/i)
    //   .filter(Boolean);

    return data.filter((row) => {
      // Column filters
      const columnMatch = columnFilters.every(({ id, value }) =>
        normalizeText(row[id]).includes(normalizeText(value))
      );

      // Global filter: every meaningful word must match somewhere
      const globalMatch =
        normalizedSearch.length > 0
          ? Object.values(row).some((val) =>
            normalizeText(val).includes(normalizedSearch)
          )
          : true;

      return columnMatch && globalMatch;
    });
  }, [data, columnFilters, globalFilter]);

  // Update parent with filtered data
  useEffect(() => {

    if (exportExcel) {
      setFilteredData(filteredRows);
    }
  }, [filteredRows, exportExcel, setFilteredData]);
  const mergedInitialState = {
    showColumnFilters: false,
    density: toggleDensity ? "compact" : null,
    ...(incomingInitialState || {}),
  };

  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={filteredRows}
        enableColumnResizing
        columnResizeMode="onChange"
        defaultColumn={{
          minSize: 100,
          size: 200,
          maxSize: 400,
        }}
        initialState={mergedInitialState}
        muiTableContainerProps={{
          id: "tble",
        }}
        muiNoDataRowProps={{
          sx: {
            backgroundColor: darkMode ? "#202940" : "#fff",
          },
        }}
        muiNoDataCellProps={{
          sx: {
            color: darkMode ? "#ffffff" : "#000000",
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: "14px",
            fontWeight: "500",
          },
        }}
        muiTableHeadCellFilterTextFieldProps={{
          sx: {
            textTransform: "none",
            "& .MuiInputLabel-root": {
              textTransform: "none",
            }
          }
        }}
        muiTableProps={{
          sx: darkMode
            ? {
              backgroundColor: "#202940",
              color: "#ffffff",
              "& td": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "14px",
                fontWeight: "500",
                padding: 0,
                color: "#ffffff",
              },
            }
            : {
              "& td": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "14px",
                fontWeight: "500",
                padding: 0,
                backgroundColor: "#f5f5f5",
              },
            },
        }}
        muiTopToolbarProps={{
          sx: darkMode
            ? {
              color: "#ffffff",
              "& svg": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "14px",
                fontWeight: "500",
                padding: 0,
                color: "#ffffff",
              },
            }
            : {
              backgroundColor: "#f5f5f5",
            },
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: darkMode ? "#202940" : "#f5f5f5",
            color: darkMode ? "#ffffff" : "#000000",
            "& svg": {
              color: darkMode ? "#ffffff" : "#000000",
            },
          },
        }}
        muiBottomToolbarProps={{
          sx: darkMode
            ? {
              color: "#ffffff",
              "& p,button,div": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: "500",
                padding: 0,
                color: "#ffffff",
              },
            }
            : {
              backgroundColor: "#f5f5f5",
            },
        }}
        muiTableBodyCellProps={{
          sx: {
            borderBottom: "2px solid #e0e0e0", // Add a border between columns
          },
        }}
        state={{
          columnVisibility,
          globalFilter,
          columnFilters,
        }}
        onColumnVisibilityChange={(newState) => {
          setColumnVisibility(newState);
          if (typeof setVisibleColumns === "function") {
            setVisibleColumns(newState);
          }
        }}
        onGlobalFilterChange={(value) =>
          setGlobalFilter((value || "").trim())
        }
        onColumnFiltersChange={setColumnFilters}
        {...rest} // Pass other props dynamically
      />
      {darkMode && (
        <GlobalStyles
          styles={{
            ".MuiTypography-body1": {
              color: "#ffffff !important", // Change the icon color to white
            },
            ".MuiTableCell-root .MuiInputLabel-root": {
              textTransform: "lowercase !important",
            },
          }}
        />
      )}
    </>
  );
};

export default CustomMaterialTable;
